import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { History } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { type RecentPost, loadRecentPosts } from '@/lib/recent-posts';

function formatRelative(ms: number): string {
  const diff = Date.now() - ms;
  const min = Math.round(diff / 60_000);
  if (min < 1) return 'เมื่อสักครู่';
  if (min < 60) return `${min} นาทีที่แล้ว`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} ชั่วโมงที่แล้ว`;
  const day = Math.round(hr / 24);
  return `${day} วันที่แล้ว`;
}

/**
 * Surfaces the per-device localStorage trail written by `trackPostVisit`
 * on every post-edit page mount. Same data the Cmd+K palette uses when
 * opened with no query — duplicated here so editors who don't know the
 * keyboard shortcut still get the "where was I just now?" jump.
 *
 * Per-device by design (no server-side recent-edits log). New devices
 * see an empty list until the editor visits a post. Renders nothing in
 * that case rather than a "no recents" placeholder — silence is the
 * right signal.
 */
export function RecentsWidget() {
  // One-time load on mount. Dashboard re-mounts on every navigation
  // back, so the list refreshes naturally without needing a storage
  // event subscription.
  const [items] = useState<RecentPost[]>(() => loadRecentPosts().slice(0, 5));

  if (items.length === 0) return null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <History className="h-4 w-4 text-slate-400" />
          เพิ่งเปิดล่าสุด
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {items.map((p) => (
            <li key={p.id} className="text-sm">
              <Link
                to="/posts/$postId"
                params={{ postId: p.id }}
                className="hover:text-brand-600 dark:hover:text-brand-400 block text-slate-700 dark:text-slate-300"
              >
                <div className="truncate font-medium">{p.title}</div>
                <div className="flex items-center gap-2 text-[11px] text-slate-400">
                  <span className="truncate font-mono">/{p.slug}</span>
                  <span className="shrink-0">· {formatRelative(p.visitedAt)}</span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
